import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { ONESIGNAL_CONFIG } from '../config';
import '../types/onesignal.d.ts';

// ============================================
// Types
// ============================================

interface PushNotificationPayload {
  title: string;
  message: string;
  url?: string;
  data?: Record<string, any>;
}

interface UsePushNotificationsReturn {
  isSubscribed: boolean;
  isSupported: boolean;
  isInitialized: boolean;
  enablePushNotifications: () => Promise<void>;
  sendLocalNotification: (payload: PushNotificationPayload) => void;
  error: string | null;
}

// ============================================
// Helper to detect iOS
// ============================================
const isIOS = () => {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) || 
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
};

const isStandalone = () => {
  return (window.matchMedia('(display-mode: standalone)').matches) ||
    ((window.navigator as any).standalone === true);
};

// ============================================
// Hook
// ============================================

export const usePushNotifications = (): UsePushNotificationsReturn => {
  const { currentUser } = useAuth();
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isSupported, setIsSupported] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Check if notifications are supported
  useEffect(() => {
    // iOS 16.4+ supports web push in PWAs
    // Consider iOS devices as supported since they can use notifications in PWA mode
    const supported = ('Notification' in window && 'serviceWorker' in navigator) || isIOS();
    setIsSupported(supported);

    if (supported && 'Notification' in window) {
      setIsSubscribed(Notification.permission === 'granted');
    }

    // Log for debugging
    console.log('[Push] Support check:', {
      supported,
      isIOS: isIOS(),
      isStandalone: isStandalone(),
      permission: 'Notification' in window ? Notification.permission : 'N/A',
    });
  }, []);

  // Handle notification clicks
  useEffect(() => {
    if (!isSupported) return;

    const handleNotificationClick = (event: MessageEvent) => {
      if (event.data?.type === 'NOTIFICATION_CLICK' && event.data?.url) {
        const url = event.data.url;
        if (url.startsWith('/')) {
          window.location.hash = url;
        } else {
          window.location.href = url;
        }
      }
    };

    navigator.serviceWorker?.addEventListener('message', handleNotificationClick);
    
    return () => {
      navigator.serviceWorker?.removeEventListener('message', handleNotificationClick);
    };
  }, [isSupported]);

  // Initialize OneSignal
  useEffect(() => {
    if (!ONESIGNAL_CONFIG.appId) {
      console.log('[Push] OneSignal not configured');
      setIsInitialized(true);
      return;
    }

    if (!isSupported) {
      console.log('[Push] Notifications not supported');
      setIsInitialized(true);
      return;
    }

    // Load OneSignal SDK dynamically if not already loaded
    const loadOneSignalSDK = () => {
      return new Promise<void>((resolve) => {
        if ((window as any).OneSignal && !(window as any).OneSignal.push) {
          resolve();
          return;
        }

        // Check if script already exists
        if (document.querySelector('script[src*="OneSignalSDK"]')) {
          resolve();
          return;
        }

        const script = document.createElement('script');
        script.src = 'https://cdn.onesignal.com/sdks/web/v16/OneSignalSDK.page.js';
        script.defer = true;
        script.onload = () => resolve();
        script.onerror = () => {
          console.error('[Push] Failed to load OneSignal SDK');
          resolve();
        };
        document.head.appendChild(script);
      });
    };

    const initOneSignal = async () => {
      try {
        await loadOneSignalSDK();
        
        window.OneSignal = window.OneSignal || [];
        
        await new Promise<void>((resolve) => {
          window.OneSignal.push(async () => {
            try {
              const OneSignal = window.OneSignal as any;
              
              // Check if already initialized
              if (OneSignal.initialized) {
                console.log('[Push] OneSignal already initialized');
                resolve();
                return;
              }

              console.log('[Push] Initializing OneSignal with appId:', ONESIGNAL_CONFIG.appId);
              
              await OneSignal.init({
                appId: ONESIGNAL_CONFIG.appId,
                allowLocalhostAsSecureOrigin: true,
                serviceWorkerParam: { scope: '/' },
                serviceWorkerPath: '/OneSignalSDKWorker.js',
                promptOptions: {
                  slidedown: {
                    prompts: [
                      {
                        type: 'push',
                        autoPrompt: false,
                        text: {
                          actionMessage: 'Get notified when you are assigned to projects and tasks',
                          acceptButton: 'Allow',
                          cancelButton: 'Later',
                        },
                      },
                    ],
                  },
                },
                welcomeNotification: {
                  title: 'OB WorkFlow',
                  message: 'Thanks for subscribing! You will now receive task notifications.',
                },
              });

              console.log('[Push] OneSignal initialized successfully');

              // Check subscription state
              const permission = await OneSignal.Notifications.permission;
              const optedIn = await OneSignal.User?.PushSubscription?.optedIn;
              
              console.log('[Push] Permission:', permission, 'OptedIn:', optedIn);
              setIsSubscribed(permission === true || optedIn === true);

              // Listen for subscription changes
              OneSignal.User?.PushSubscription?.addEventListener('change', (event: any) => {
                console.log('[Push] Subscription changed:', event);
                setIsSubscribed(event.current?.optedIn ?? false);
              });

              // Handle notification clicks
              OneSignal.Notifications?.addEventListener('click', (event: any) => {
                console.log('[Push] Notification clicked:', event);
                const url = event.notification?.launchURL || event.notification?.data?.url;
                if (url) {
                  if (url.startsWith('/')) {
                    window.location.hash = url;
                  } else {
                    window.location.href = url;
                  }
                }
              });

              resolve();
            } catch (err) {
              console.error('[Push] OneSignal init error:', err);
              resolve();
            }
          });
        });

        setIsInitialized(true);
      } catch (err) {
        console.error('[Push] OneSignal setup failed:', err);
        setIsInitialized(true);
      }
    };

    initOneSignal();
  }, [isSupported]);

  // Login user to OneSignal when authenticated
  useEffect(() => {
    if (!currentUser || !isInitialized || !ONESIGNAL_CONFIG.appId) return;

    const loginToOneSignal = async (retryCount = 0): Promise<void> => {
      const maxRetries = 3;
      const retryDelay = 1000; // 1 second

      try {
        const OneSignal = window.OneSignal as any;

        // Wait for OneSignal to be ready (not an array anymore)
        if (!OneSignal || Array.isArray(OneSignal) || !OneSignal.login) {
          if (retryCount < maxRetries) {
            console.log(`[Push] OneSignal not ready, retrying in ${retryDelay}ms (attempt ${retryCount + 1}/${maxRetries})`);
            await new Promise(resolve => setTimeout(resolve, retryDelay));
            return loginToOneSignal(retryCount + 1);
          }
          console.warn('[Push] OneSignal not ready after max retries');
          return;
        }

        console.log('[Push] Logging in user:', currentUser.id);

        // Login with external user ID (this sets the External ID in OneSignal)
        await OneSignal.login(currentUser.id);
        console.log('[Push] External ID set successfully');

        // Set user tags for targeting/segmentation
        if (OneSignal.User?.addTags) {
          await OneSignal.User.addTags({
            user_id: currentUser.id,
            user_role: currentUser.role,
            user_email: currentUser.email,
          });
          console.log('[Push] User tags set successfully:', {
            user_id: currentUser.id,
            user_role: currentUser.role,
            user_email: currentUser.email,
          });
        }

      } catch (err) {
        console.error('[Push] OneSignal login failed:', err);
        // Retry on failure
        if (retryCount < maxRetries) {
          console.log(`[Push] Retrying login in ${retryDelay}ms (attempt ${retryCount + 1}/${maxRetries})`);
          await new Promise(resolve => setTimeout(resolve, retryDelay));
          return loginToOneSignal(retryCount + 1);
        }
      }
    };

    loginToOneSignal();
  }, [currentUser?.id, currentUser?.role, currentUser?.email, isInitialized]);

  // Send a local notification
  const sendLocalNotification = useCallback((payload: PushNotificationPayload) => {
    if (!isSupported || Notification.permission !== 'granted') {
      return;
    }

    try {
      if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.postMessage({
          type: 'SHOW_NOTIFICATION',
          payload: {
            title: payload.title,
            body: payload.message,
            icon: '/icon.png',
            badge: '/icon.png',
            tag: payload.data?.id || Date.now().toString(),
            data: { url: payload.url || '/', ...payload.data },
          },
        });
      } else {
        const notification = new Notification(payload.title, {
          body: payload.message,
          icon: '/icon.png',
        });
        notification.onclick = () => {
          window.focus();
          if (payload.url) window.location.hash = payload.url;
          notification.close();
        };
      }
    } catch (err) {
      console.error('[Push] Notification failed:', err);
    }
  }, [isSupported]);

  // Enable push notifications
  const enablePushNotifications = useCallback(async () => {
    if (!isSupported) {
      setError('Push notifications are not supported');
      return;
    }

    // iOS PWA check
    if (isIOS() && !isStandalone()) {
      setError('On iOS, please add this app to your Home Screen first, then enable notifications.');
      return;
    }

    setError(null);

    try {
      if (ONESIGNAL_CONFIG.appId && isInitialized) {
        console.log('[Push] Requesting permission via OneSignal...');
        
        await new Promise<void>((resolve, reject) => {
          const handleRequest = async () => {
            try {
              const OneSignal = window.OneSignal as any;
              
              // Request permission
              const permissionResult = await OneSignal.Notifications.requestPermission();
              console.log('[Push] Permission request result:', permissionResult);

              // Check the actual permission state (more reliable on iOS)
              const actualPermission = await OneSignal.Notifications.permission;
              const nativePermission = 'Notification' in window ? Notification.permission : null;

              console.log('[Push] Actual permission state:', {
                oneSignal: actualPermission,
                native: nativePermission
              });

              // Permission is granted if either OneSignal or native permission is granted
              const isGranted = actualPermission === true || nativePermission === 'granted';

              if (isGranted) {
                // Opt in to push
                await OneSignal.User?.PushSubscription?.optIn();

                // Get subscription ID
                const subscriptionId = await OneSignal.User?.PushSubscription?.id;
                console.log('[Push] Subscription ID:', subscriptionId);

                setIsSubscribed(true);

                // Set External ID and tags after successful subscription
                if (currentUser) {
                  console.log('[Push] Setting External ID after subscription:', currentUser.id);
                  await OneSignal.login?.(currentUser.id);

                  // Set user tags for targeting
                  if (OneSignal.User?.addTags) {
                    await OneSignal.User.addTags({
                      user_id: currentUser.id,
                      user_role: currentUser.role,
                      user_email: currentUser.email,
                    });
                    console.log('[Push] Tags set after subscription:', {
                      user_id: currentUser.id,
                      user_role: currentUser.role,
                      user_email: currentUser.email,
                    });
                  }
                }
              } else {
                setError('Notification permission was denied');
              }
              
              resolve();
            } catch (err) {
              reject(err);
            }
          };

          if (Array.isArray(window.OneSignal)) {
            window.OneSignal.push(handleRequest);
          } else {
            handleRequest();
          }
        });
      } else {
        // Fallback to native notifications
        const permission = await Notification.requestPermission();
        
        if (permission === 'granted') {
          setIsSubscribed(true);
          sendLocalNotification({
            title: 'OB WorkFlow',
            message: 'Notifications enabled!',
            url: '/',
          });
        } else {
          setError('Notification permission was denied');
        }
      }
    } catch (err: any) {
      console.error('[Push] Enable notifications error:', err);
      setError(err.message || 'Failed to enable notifications');
    }
  }, [isSupported, isInitialized, currentUser, sendLocalNotification]);

  return {
    isSubscribed,
    isSupported,
    isInitialized,
    enablePushNotifications,
    sendLocalNotification,
    error,
  };
};

export default usePushNotifications;
